import { format } from 'date-fns'

import type { Transcoder } from '@img-arena/consoles-api-streaming/dist/transcoders'
import CheckIcon from '@mui/icons-material/Check'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import WOWZA_LOGO_PATH from '@/assets/img/wowza-logo.png'
import ZIXI_LOGO_PATH from '@/assets/img/zixi-logo.png'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import { Column, COLUMN_CELL_TYPE, Filter } from '@/components/table/base'
import { FilterType } from '@/components/table/base/FilterableTable/FilterableTable.types'
import { DetailedCell } from '@/components/table/base/TableBase/components/DetailedCell'

export const INSTANCE_TYPES_MAP: Record<string, string> = {
  CPU_LARGE: 'CPU Large',
  CPU: 'CPU',
  AUDIO: 'Audio'
} as const

export const EC2_REGIONS_MAP: Record<string, { text: string; region: string }> = {
  'eu-west-1': { text: 'Ireland', region: 'Europe' },
  'eu-central-1': { text: 'Frankfurt', region: 'Europe' },
  'us-east-1': { text: 'N. Virginia', region: 'United States' },
  'us-east-2': { text: 'Ohio', region: 'United States' },
  'us-west-1': { text: 'California', region: 'United States' },
  'us-west-2': { text: 'Oregon', region: 'United States' },
  'ca-central-1': { text: 'Canada', region: 'United States' },
  'ap-northeast-1': { text: 'Tokyo', region: 'Asia' },
  'ap-northeast-2': { text: 'Seoul', region: 'Asia' },
  'ap-southeast-1': { text: 'Singapore', region: 'Asia' },
  'ap-southeast-2': { text: 'Sydney', region: 'Asia' },
  'ap-south-1': { text: 'Mumbai', region: 'Asia' },
  'sa-east-1': { text: 'Sao Paulo', region: 'South America' }
} as const

export const TRANSCODERS_TABLE_COLUMNS: Column<Transcoder>[] = [
  {
    id: 'id',
    label: 'ID',
    type: COLUMN_CELL_TYPE.NUMBER,
    sortAttribute: 'id'
  },
  {
    id: 'useWowzaInput',
    label: 'Server',
    type: COLUMN_CELL_TYPE.COMPONENT,
    sortAttribute: 'useWowzaInput',
    render: (value) => (
      <ConsoleTooltip title={value ? 'Wowza' : 'Zixi'}>
        <img
          src={value ? WOWZA_LOGO_PATH : ZIXI_LOGO_PATH}
          alt={value ? 'Wowza' : 'Zixi'}
          height={24}
        />
      </ConsoleTooltip>
    ),
    comparator: (a, b) => {
      const inputA = a ? 'Wowza' : 'Zixi'
      const inputB = b ? 'Wowza' : 'Zixi'
      return inputA.localeCompare(inputB)
    }
  },
  {
    id: 'ec2Region',
    label: 'Region',
    type: COLUMN_CELL_TYPE.COMPONENT,
    sortAttribute: 'ec2Region',
    render: (value) => {
      const regionData = EC2_REGIONS_MAP[value]
      if (!regionData) return
      return <DetailedCell value={regionData.text} subValue={value} />
    },
    comparator: (a, b) => {
      const regionA = EC2_REGIONS_MAP[a]?.text || a
      const regionB = EC2_REGIONS_MAP[b]?.text || b
      return regionA.localeCompare(regionB)
    }
  },
  {
    id: 'instanceType',
    label: 'Type',
    type: COLUMN_CELL_TYPE.COMPONENT,
    sortAttribute: 'instanceType',
    render: (value) => {
      const label = INSTANCE_TYPES_MAP[value]
      if (!label) return
      return <Chip label={label} size="small" variant="outlined" color="primary" />
    },
    comparator: (a, b) => a.localeCompare(b)
  },
  {
    id: '$status',
    label: 'Status',
    type: COLUMN_CELL_TYPE.COMPONENT,
    render: (value) => {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {value.available && (
            <ConsoleTooltip title="Transcoder is Available">
              <CheckIcon />
            </ConsoleTooltip>
          )}
          {value.reserved ? (
            <ConsoleTooltip title="Transcoder is Reserved">
              <LockIcon />
            </ConsoleTooltip>
          ) : (
            <ConsoleTooltip title="Transcoder is Unreserved">
              <LockOpenIcon />
            </ConsoleTooltip>
          )}
          {value.disabled ? (
            <ConsoleTooltip title="Transcoder is Disabled">
              <PauseIcon />
            </ConsoleTooltip>
          ) : (
            <ConsoleTooltip title="Transcoder is Enabled">
              <PlayArrowIcon />
            </ConsoleTooltip>
          )}
        </Stack>
      )
    }
  },
  {
    id: 'activeStreamsCount',
    label: 'Streams',
    type: COLUMN_CELL_TYPE.NUMBER,
    sortAttribute: 'activeStreamsCount'
  },
  {
    id: 'lastHeartBeat',
    label: 'Last Heartbeat',
    type: COLUMN_CELL_TYPE.COMPONENT,
    sortAttribute: 'lastHeartBeat',
    render: (value) => {
      const date = new Date(value)
      if (isNaN(date.getTime())) return
      const time = format(date, 'hh:mm a')
      const fullDate = format(date, 'EEE, MMM dd yyyy')
      return <DetailedCell value={time} subValue={fullDate} />
    },
    comparator: (a, b) => Number(new Date(a)) - Number(new Date(b))
  },
  {
    id: 'ec2InstanceId',
    label: 'EC2 Instance ID',
    type: COLUMN_CELL_TYPE.TEXT,
    sortAttribute: 'ec2InstanceId'
  },
  {
    id: 'ip',
    label: 'IP Address',
    type: COLUMN_CELL_TYPE.TEXT,
    sortAttribute: 'ip'
  },
  {
    id: 'privateIp',
    label: 'Private IP Address',
    type: COLUMN_CELL_TYPE.TEXT,
    sortAttribute: 'privateIp'
  },
  {
    id: 'initiatedBy',
    label: 'Initiated By',
    type: COLUMN_CELL_TYPE.TEXT,
    sortAttribute: 'initiatedBy'
  }
]

const getOptions = (data: Transcoder[]) => {
  const options = data.reduce(
    (
      acc: Record<
        | 'inputTypeOptions'
        | 'instanceTypeOptions'
        | 'ec2RegionOptions'
        | 'initiatedByOptions'
        | 'statusOptions',
        Record<string, FilterType>
      >,
      item
    ) => {
      const { useWowzaInput, instanceType, ec2Region, initiatedBy, reserved } = item

      const inputTypeKey = String(useWowzaInput)
      if (!acc.inputTypeOptions[inputTypeKey]) {
        acc.inputTypeOptions[inputTypeKey] = {
          value: inputTypeKey,
          label: useWowzaInput ? 'Wowza' : 'Zixi'
        }
      }

      if (!acc.instanceTypeOptions[instanceType]) {
        acc.instanceTypeOptions[instanceType] = {
          value: instanceType,
          label: INSTANCE_TYPES_MAP[instanceType] || instanceType
        }
      }

      if (!acc.ec2RegionOptions[ec2Region]) {
        const regionData = EC2_REGIONS_MAP[ec2Region]
        acc.ec2RegionOptions[ec2Region] = {
          value: ec2Region,
          label: regionData ? `${regionData.text} (${ec2Region})` : ec2Region
        }
      }

      if (!acc.initiatedByOptions[initiatedBy]) {
        acc.initiatedByOptions[initiatedBy] = {
          value: initiatedBy,
          label: initiatedBy
        }
      }

      const statusKey = String(reserved)
      if (!acc.statusOptions[statusKey]) {
        acc.statusOptions[statusKey] = {
          value: statusKey,
          label: reserved ? 'Reserved' : 'Unreserved'
        }
      }

      return acc
    },
    {
      inputTypeOptions: {},
      instanceTypeOptions: {},
      ec2RegionOptions: {},
      initiatedByOptions: {},
      statusOptions: {}
    }
  )

  return {
    inputTypeOptions: Object.values(options.inputTypeOptions),
    instanceTypeOptions: Object.values(options.instanceTypeOptions),
    ec2RegionOptions: Object.values(options.ec2RegionOptions),
    initiatedByOptions: Object.values(options.initiatedByOptions),
    statusOptions: Object.values(options.statusOptions)
  }
}

export const getTranscodersTableFilters = (data: Transcoder[]): Filter<Transcoder>[] => {
  const {
    inputTypeOptions,
    instanceTypeOptions,
    ec2RegionOptions,
    initiatedByOptions,
    statusOptions
  } = getOptions(data)

  return [
    {
      type: 'freeTextSearch',
      paramName: 'filter',
      placeholder: 'Search',
      filterOptions: (row, search) => {
        if (!search) return true
        const lowerCaseSearch = search.toLowerCase()
        const { id, ec2InstanceId, ip, privateIp, initiatedBy, ec2Region, instanceType } = row
        const fields = [
          id,
          ec2InstanceId,
          ip,
          privateIp,
          initiatedBy,
          ec2Region,
          instanceType,
          EC2_REGIONS_MAP[ec2Region]?.text
        ]
        return fields.some((field) => (String(field).toLowerCase() || '').includes(lowerCaseSearch))
      }
    },
    {
      type: 'select',
      paramName: 'inputType',
      label: 'Server Type',
      options: inputTypeOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { useWowzaInput } = item
        return String(useWowzaInput) === filter.value
      }
    },
    {
      type: 'select',
      paramName: 'instanceType',
      label: 'Instance Type',
      options: instanceTypeOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { instanceType } = item
        return instanceType === filter.value
      }
    },
    {
      type: 'select',
      paramName: 'ec2Region',
      label: 'AWS Region',
      options: ec2RegionOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { ec2Region } = item
        return ec2Region === filter.value
      }
    },
    {
      type: 'select',
      paramName: 'status',
      label: 'Status',
      options: statusOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { reserved } = item
        return (reserved && filter.value === 'true') || (!reserved && filter.value === 'false')
      }
    },
    {
      type: 'select',
      paramName: 'initiatedBy',
      label: 'Initiated By',
      options: initiatedByOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { initiatedBy } = item
        return initiatedBy === filter.value
      }
    }
  ]
}
